/** Tweakable day/night cycle, night look, and outdoor light radii. */

export const DAY_NIGHT = {
  dayDurationMs: 15 * 60 * 1000,
  nightDurationMs: 8 * 60 * 1000,
  dawnDurationMs: 150 * 1000,
  duskDurationMs: 150 * 1000,
  // null = start at the middle of the day phase.
  startOffsetMs: null,

  // Both grades share one shape: contrast, then hue/sat/brightness in HSL,
  // then an RGB blend toward `tint` by `tintStrength`. The tint is what sets
  // the overall cast — hue rotation alone can't reach blue from this palette.
  night: {
    brightness: 0.52,
    saturation: 0.5,
    hueShiftDeg: 0,
    contrast: 1.05,
    tint: '#2c3f7c',
    tintStrength: 0.42,
  },

  // Dusk/dawn pass through this warm grade at nightStrength 0.5; crossfading
  // it into the blue night grade sweeps the scene through orange then purple.
  dusk: {
    brightness: 0.88,
    saturation: 1.0,
    hueShiftDeg: -10,
    contrast: 1.02,
    tint: '#c9571f',
    tintStrength: 0.22,
  },

  // Optional per-index overrides after the transform (same 32 slots as ART_PALETTE).
  // Transparent (0) and Ink (1) stay unchanged unless overridden here.
  // `paletteOverrides` applies to the night grade, `duskPaletteOverrides` to dusk.
  paletteOverrides: {},
  duskPaletteOverrides: {},

  // How dark a sealed interior sits, independent of the time of day. 1 puts an
  // unlit room at the full night grade at every hour, so a windowless, lampless
  // house is dark at noon. Windows and lamps carve this back toward daylight.
  interiorShade: 1,

  // Used only if WebGL compositing is unavailable.
  fallbackTint: '#1a2744',
  fallbackDuskTint: '#8a4520',
  fallbackMaxAlpha: 0.72,

  lights: {
    falloff: 'smoothstep',
    coreRadiusTiles: 0.5,
    defaultRadiusTiles: 3.5,
    // Emitters live on more than one channel: lamps and stoves stand on the
    // floor, sconces hang on a wall.
    channels: ['onGround', 'wallObject'],
    byTileKey: {
      'object.lamp-post': { radiusTiles: 4, intensity: 1, origin: { x: 0.5, y: -0.65 } },
      'object.twin-lamp-post': { radiusTiles: 4.5, intensity: 1, origin: { x: 0.5, y: -0.65 } },
      'object.festival-light-post': { radiusTiles: 4, intensity: 1, origin: { x: 0.5, y: -0.65 } },
      'object.campfire': { radiusTiles: 2.5, intensity: 1, origin: { x: 0.5, y: 0.45 } },
      // Indoor emitters burn at every hour: an unlit room is dark at noon too,
      // so a lamp is what makes a windowless house habitable by daylight.
      'interior-object.standing-lamp': { radiusTiles: 3.5, intensity: 1, origin: { x: 0.5, y: 0.3 } },
      // A back-wall object draws at its own row with the wall's lower half one
      // row south (renderer._drawFrame), so the room starts a full cell below.
      'interior-wall-object.sconce': { radiusTiles: 3, intensity: 0.9, origin: { x: 0.5, y: 1 } },
    },
  },

  // Windows are daylight openings, not lamps: they throw a directional shaft
  // into the room and fade out as night falls.
  windows: {
    lengthTiles: 5.5,
    spreadTiles: 2.6,
    mouthRadiusTiles: 1.2,
    intensity: 1,
    byTileKey: {
      'exterior-object.window-square': {},
      'exterior-object.window-round': { spreadTiles: 2, intensity: 0.85 },
      'interior-wall-object.window': { intensity: 0.8 },
    },
  },
};
